/**
 * InflowPay Payment SDK v2
 * 
 * Provider class that manages global SDK configuration
 * Similar to the original SDK but uses iframe-based payment flow
 */

import { CardElement } from './card-element';
import type { PaymentResult } from './react';
import type { CSSProperties, Locale } from './types';
import { getEnvironmentFromApiKey, getIframeUrlFromApiKey } from './utils';

export interface PaymentSDKConfig {
  /** Public API key */
  publicKey: string;
  /** Locale for the UI. Defaults to 'en' */
  locale?: Locale;
}

export class PaymentSDK {
  private config: PaymentSDKConfig;
  private iframeUrl: string;
  private timeout: number;
  private debug: boolean;

  /**
   * Initialize the InflowPay Payment SDK
   * 
   * @param config - SDK configuration
   * 
   * @example
   * ```typescript
   * const sdk = new PaymentSDK({
   *   apiKey: 'inflow_pub_local_xxx'
   * });
   * ```
   */
  constructor(config: PaymentSDKConfig) {
    // Validate API key
    if (!config.publicKey || typeof config.publicKey !== 'string') {
      throw new Error('API key is required');
    }

    this.config = config;
    this.timeout = 30000;

    // Auto-detect iframe URL from API key using utility function
    this.iframeUrl = getIframeUrlFromApiKey(config.publicKey);

    // Auto-enable debug in development and sandbox environments
    const environment = getEnvironmentFromApiKey(config.publicKey);
    this.debug = environment === 'sandbox' || environment === 'development';
  }

  /**
   * Create a CardElement for iframe-based payment UI
   * 
   * @param options - CardElement configuration
   * @returns CardElement instance
   * 
   * @example
   * ```typescript
   * const cardElement = sdk.createCardElement({
   *   container: '#card-container',
   *   paymentId: 'pay_123',
   *   onComplete: (result) => {
   *     if (result.status === 'CHECKOUT_SUCCESS') {
   *       window.location.href = '/success';
   *     }
   *   }
   * });
   * 
   * cardElement.mount();
   * ```
   */
  createCardElement(options: {
    container: string | HTMLElement;
    paymentId: string;
    style?: CSSProperties;
    buttonText?: string;
    placeholders?: {
      cardNumber?: string;
      expiry?: string;
      cvc?: string;
    };
    onComplete?: (result: PaymentResult) => void;
    onError?: (error: any) => void;
    onClose?: () => void;
  }): CardElement {
    return new CardElement({
      publicKey: this.config.publicKey,
      iframeUrl: this.iframeUrl,
      timeout: this.timeout,
      debug: this.debug,
      locale: this.config.locale,
    }, options);
  }

  /**
   * Get the iframe URL being used
   */
  getIframeUrl(): string {
    return this.iframeUrl;
  }

  /**
   * Get the API key
   */
  getApiKey(): string {
    return this.config.publicKey;
  }
}

